import { ROLES, hasCapability, isSubscribed } from '../../../utils/roles.js';
import { M } from '../msg.js';
import { registerPageTour } from '../matchRoute.js';

const STAFF = [ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER];

registerPageTour({
  id: 'admin-dashboard',
  match: (p) => p === '/admin',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Admin dashboard', 'Dashboard администратора'),
      body: M(
        'Your morning overview: counts, deadlines, integration health, and quick links. Red numbers need attention today.',
        'Утренний обзор: счётчики, дедлайны, интеграции и быстрые ссылки. Красные цифры требуют внимания.',
      ),
      roles: STAFF,
      when: isSubscribed,
    },
    {
      id: 'stats',
      selector: '[data-tour="tour-dashboard-stats"]',
      title: M('Key metrics', 'Ключевые метрики'),
      body: M(
        'Clients, active projects, pending forms, asset requests, and clients near time limits. Click through from quick links below.',
        'Клиенты, проекты, формы, asset requests и клиенты у лимита часов. Ниже — быстрые ссылки.',
      ),
      roles: STAFF,
      when: isSubscribed,
    },
    {
      id: 'announcements',
      selector: '[data-tour="dashboard-announcements"]',
      title: M('Company news', 'Новости агентства'),
      body: M(
        'Admins publish announcements here; clients see them on their dashboard. Use for launches, maintenance, or policy updates.',
        'Admin публикует объявления; клиенты видят их на dashboard. Запуски, maintenance, политики.',
      ),
      roles: [ROLES.ADMIN],
      when: (u) => isSubscribed(u) && hasCapability(u, 'manage_announcements'),
    },
    {
      id: 'integrations-panel',
      selector: '[data-tour="dashboard-integrations"]',
      title: M('Integration status', 'Статус интеграций'),
      body: M(
        'Quick check which providers are configured. Open Integrations in Settings for keys, ping tests, and sync.',
        'Быстрая проверка настроенных провайдеров. Полные ключи и ping — в Integrations.',
      ),
      roles: [ROLES.ADMIN],
      when: isSubscribed,
    },
  ],
});

registerPageTour({
  id: 'admin-billing',
  match: (p) => p === '/admin/billing',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="tour-billing-main"]',
      title: M('Agency subscription', 'Подписка агентства'),
      body: M(
        'Your agency pays Viib here — separate from Stripe keys for your clients. Without an active plan, staff cannot use Clients or Projects.',
        'Здесь агентство платит за Viib — не путать со Stripe для клиентов. Без плана staff не работает в Clients/Projects.',
      ),
      roles: [ROLES.ADMIN],
    },
    {
      id: 'plans',
      selector: '[data-tour="billing-plans"]',
      title: M('Choose a plan', 'Выбор плана'),
      body: M(
        'Compare Starter vs Pro feature lists. Subscribe opens Stripe Checkout. After payment, refresh — /admin unlocks.',
        'Сравните Starter и Pro. Subscribe → Stripe Checkout. После оплаты обновите страницу.',
      ),
      roles: [ROLES.ADMIN],
    },
    {
      id: 'current',
      selector: '[data-tour="billing-current-plan"]',
      title: M('Current plan', 'Текущий план'),
      body: M(
        'When subscribed, see included features and manage billing via Stripe Customer Portal.',
        'При активной подписке — включённые features и Stripe Customer Portal.',
      ),
      roles: [ROLES.ADMIN],
      when: isSubscribed,
    },
  ],
});

registerPageTour({
  id: 'admin-clients',
  match: (p) => p === '/admin/clients',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Clients list', 'Список клиентов'),
      body: M(
        'Every project, portal user, and FAQ hub links to a client record. Managers may see only assigned clients.',
        'К каждому клиенту привязаны проекты, portal users и FAQ. Managers видят назначенных клиентов.',
      ),
      roles: STAFF,
      capabilities: ['manage_clients'],
    },
    {
      id: 'search',
      selector: '[data-tour="clients-search"]',
      title: M('Search', 'Поиск'),
      body: M(
        'Filter by name, company, or email. Press Search to reload the table.',
        'Фильтр по имени, компании или email. Search — обновить таблицу.',
      ),
      roles: STAFF,
      capabilities: ['manage_clients'],
    },
    {
      id: 'table',
      selector: '[data-tour="clients-table"]',
      title: M('Client table', 'Таблица клиентов'),
      body: M(
        'Open a row to manage projects, FAQ, portal logins, documents, and integration sync for that client.',
        'Open — проекты, FAQ, логины портала, документы и sync интеграций.',
      ),
      roles: STAFF,
      capabilities: ['manage_clients'],
    },
    {
      id: 'create',
      selector: '[data-tour="clients-create"]',
      title: M('Create client', 'Создать клиента'),
      body: M(
        'Minimum: name. Add email and phone for contacts. After create, open the client to add projects and portal users.',
        'Минимум — имя. Email и телефон для контактов. После создания откройте карточку для проектов и portal users.',
      ),
      roles: STAFF,
      capabilities: ['manage_clients'],
    },
  ],
});

registerPageTour({
  id: 'admin-client-detail',
  match: (p) => /^\/admin\/clients\/\d+$/.test(p),
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Client record', 'Карточка клиента'),
      body: M(
        'Central hub for one customer: integrations, portal logins, FAQ, documents, and project list.',
        'Центр работы с одним заказчиком: интеграции, логины, FAQ, документы и проекты.',
      ),
      roles: STAFF,
      capabilities: ['manage_clients'],
    },
    {
      id: 'sync',
      selector: '[data-tour="client-sync"]',
      title: M('Integration sync', 'Синхронизация'),
      body: M(
        'Pull data from GHL, Bitwarden, or Teams/SharePoint when IDs are set. Sync all runs every configured target.',
        'Загрузка из GHL, Bitwarden или Teams при настроенных ID. Sync all — все цели.',
      ),
      roles: STAFF,
      capabilities: ['manage_clients'],
    },
    {
      id: 'portal-users',
      selector: '[data-tour="client-portal-users"]',
      title: M('Portal logins', 'Логины портала'),
      body: M(
        'Create client portal accounts: invite by email or set a password. View as client previews /my. Deactivate revokes access.',
        'Аккаунты /my: invite или пароль. View as client — проверка портала. Deactivate — отзыв доступа.',
      ),
      roles: STAFF,
      capabilities: ['manage_portal_users'],
    },
    {
      id: 'faq',
      selector: '[data-tour="client-faq"]',
      title: M('FAQ & forms', 'FAQ и формы'),
      body: M(
        'Build categories, credentials blocks, file uploads, and intake forms. Plan controls which block types are allowed.',
        'Категории, credentials, файлы и формы. Типы блоков зависят от плана.',
      ),
      roles: [ROLES.ADMIN, ROLES.MANAGER],
      capabilities: ['manage_client_faq'],
    },
    {
      id: 'projects',
      selector: '[data-tour="client-projects"]',
      title: M('Projects', 'Проекты'),
      body: M(
        'Linked projects for this client. Create new projects pre-filtered to this client from the link below.',
        'Проекты клиента. Ссылка ниже создаёт проект с уже выбранным клиентом.',
      ),
      roles: STAFF,
      capabilities: ['manage_projects'],
    },
  ],
});

registerPageTour({
  id: 'admin-projects',
  match: (p) => p === '/admin/projects',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Projects', 'Проекты'),
      body: M(
        'Deliverables per client: status, media, FAQ, SharePoint, integration widgets, and activity log.',
        'Работы по клиенту: статус, media, FAQ, SharePoint, виджеты и activity.',
      ),
      roles: STAFF,
      capabilities: ['manage_projects'],
    },
    {
      id: 'filter',
      selector: '[data-tour="projects-filter"]',
      title: M('Filter by client', 'Фильтр по клиенту'),
      body: M(
        'Narrow the list to one client — useful when managing a large portfolio.',
        'Сузить список до одного клиента.',
      ),
      roles: STAFF,
      capabilities: ['manage_projects'],
    },
    {
      id: 'list',
      selector: '[data-tour="projects-list"]',
      title: M('Project list', 'Список проектов'),
      body: M(
        'Open a project for tabs: Overview, Activity, Media, FAQ, Tools (Figma, GA, SEO, hosting), SharePoint.',
        'Open — вкладки Overview, Activity, Media, FAQ, Tools, SharePoint.',
      ),
      roles: STAFF,
      capabilities: ['manage_projects'],
    },
    {
      id: 'create',
      selector: '[data-tour="projects-create"]',
      title: M('New project', 'Новый проект'),
      body: M(
        'Pick client, name, type, status, and optional site URL. Hosting monitor uses the site URL when configured.',
        'Клиент, имя, тип, статус и URL сайта. Hosting monitor использует URL.',
      ),
      roles: STAFF,
      capabilities: ['manage_projects'],
    },
  ],
});

registerPageTour({
  id: 'admin-project-detail',
  match: (p) => /^\/admin\/projects\/\d+$/.test(p),
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Project workspace', 'Рабочая зона проекта'),
      body: M(
        'All delivery artifacts for one engagement live here. Clients see a subset in /my/projects.',
        'Все материалы по одному проекту. Клиент видит часть в /my/projects.',
      ),
      roles: STAFF,
      capabilities: ['manage_projects'],
    },
    {
      id: 'tabs',
      selector: '[data-tour="project-tabs"]',
      title: M('Tabs', 'Вкладки'),
      body: M(
        'Switch between overview, timeline, files, contacts, FAQ, sync, integration tools, and SharePoint folders.',
        'Overview, timeline, файлы, контакты, FAQ, sync, tools и SharePoint.',
      ),
      roles: STAFF,
      capabilities: ['manage_projects'],
    },
    {
      id: 'tools',
      selector: '[data-tour="project-tools"]',
      title: M('Tools & widgets', 'Tools и виджеты'),
      body: M(
        'Configure Figma, Google Analytics, SE Ranking, and hosting health per project. Requires plan features and Integrations keys.',
        'Figma, GA, SE Ranking и hosting на проект. Нужны plan features и ключи в Integrations.',
      ),
      roles: STAFF,
      capabilities: ['project_integrations'],
    },
  ],
});

registerPageTour({
  id: 'admin-contacts',
  match: (p) => p === '/admin/contacts',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Contacts hub', 'Хаб контактов'),
      body: M(
        'Unified directory of people across clients — from GHL sync or manual entry on client/project records.',
        'Единый справочник людей по клиентам — из GHL или ручного ввода.',
      ),
      roles: STAFF,
      capabilities: ['view_contacts'],
    },
    {
      id: 'list',
      selector: '[data-tour="contacts-list"]',
      title: M('Contact list', 'Список контактов'),
      body: M(
        'Search and open the parent client or project. Keep contacts updated for mailbox and project context.',
        'Поиск и переход к клиенту/проекту. Актуальные контакты важны для почты и проектов.',
      ),
      roles: STAFF,
      capabilities: ['view_contacts'],
    },
  ],
});

registerPageTour({
  id: 'admin-tasks',
  match: (p) => p === '/admin/tasks',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Tasks', 'Задачи'),
      body: M(
        'Track work on projects. Assign staff or portal clients (client_tasks plan). Use ?mine=1 URL or nav for your tasks only.',
        'Задачи по проектам. Назначение staff или клиентов (client_tasks). My tasks — только ваши.',
      ),
      roles: STAFF,
      capabilities: ['manage_tasks'],
    },
    {
      id: 'filters',
      selector: '[data-tour="tasks-filters"]',
      title: M('Filters', 'Фильтры'),
      body: M(
        'Filter by project, assignee, or status. Managers use this to balance workload.',
        'Фильтр по проекту, исполнителю и статусу.',
      ),
      roles: STAFF,
      capabilities: ['manage_tasks'],
    },
    {
      id: 'list',
      selector: '[data-tour="tasks-list"]',
      title: M('Task board', 'Доска задач'),
      body: M(
        'Drag cards between To Do, In Progress, Waiting, and Complete. Open a card for the client, priority, checklist, and due date.',
        'Перетаскивайте карточки между колонками. В карточке — описание, срок и исполнитель.',
      ),
      roles: STAFF,
      capabilities: ['manage_tasks'],
    },
  ],
});

registerPageTour({
  id: 'admin-forms',
  match: (p) => p === '/admin/forms',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Forms', 'Формы'),
      body: M(
        'Intake forms across clients. Open a form for responses, or use the builder to edit questions.',
        'Формы по клиентам. Откройте форму для ответов или правьте вопросы в конструкторе.',
      ),
      roles: [ROLES.ADMIN, ROLES.MANAGER],
      capabilities: ['faq_forms'],
    },
    {
      id: 'list',
      selector: '[data-tour="forms-list"]',
      title: M('Form library', 'Библиотека форм'),
      body: M(
        'Search and filter by status. Unread counts show which forms still need a look.',
        'Поиск и фильтр по статусу. Счётчик непрочитанных показывает, что ещё нужно разобрать.',
      ),
      roles: [ROLES.ADMIN, ROLES.MANAGER],
      capabilities: ['faq_forms'],
    },
  ],
});

registerPageTour({
  id: 'admin-assets',
  match: (p) => p === '/admin/assets',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Media & assets', 'Медиа и ассеты'),
      body: M(
        'Library, uploads, client requests, collections, approvals, and AI search (Pro). Clients download approved files in /my/assets.',
        'Библиотека, загрузки, запросы, коллекции, approvals и AI search. Клиенты — в /my/assets.',
      ),
      roles: STAFF,
      capabilities: ['manage_assets'],
    },
    {
      id: 'tabs',
      selector: '[data-tour="assets-tabs"]',
      title: M('Sections', 'Разделы'),
      body: M(
        'Upload Center for bulk ingest, Requests for client asks, Collections for campaigns, AI Search when OpenAI is configured.',
        'Upload Center, Requests, Collections, AI Search при OpenAI.',
      ),
      roles: STAFF,
      capabilities: ['manage_assets'],
    },
  ],
});

registerPageTour({
  id: 'admin-integrations',
  match: (p) => p === '/admin/integrations',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Integrations', 'Интеграции'),
      body: M(
        'Connect third-party APIs for the whole agency. Cards shown depend on your subscription plan.',
        'API агентства. Карточки зависят от плана подписки.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_integrations'],
    },
    {
      id: 'cards',
      selector: '[data-tour="integrations-list"]',
      title: M('Provider list', 'Список провайдеров'),
      body: M(
        'Each row is an integration. Click Configure (or the row) to open settings, save keys, and recheck permissions.',
        'Каждая строка — интеграция. Configure (или клик по строке) открывает настройки, ключи и проверку прав.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_integrations'],
    },
    {
      id: 'routing',
      selector: '[data-tour="integrations-routing"]',
      title: M('Asset routing', 'Маршрутизация ассетов'),
      body: M(
        'Optional JSON rules to auto-sort uploads in the asset hub. Teams webhook and GHL inbound URL are configured here too.',
        'JSON-правила автосортировки загрузок. Здесь же Teams webhook и GHL inbound URL.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_integrations'],
    },
  ],
});

registerPageTour({
  id: 'admin-team',
  match: (p) => p === '/admin/team',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Team', 'Команда'),
      body: M(
        'Invite managers and team members. Assign which clients each manager can access.',
        'Приглашение managers и team members. Назначение клиентов managers.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_team'],
    },
    {
      id: 'invite',
      selector: '[data-tour="team-invite"]',
      title: M('Invitations', 'Приглашения'),
      body: M(
        'Email invite creates accept-invite link. Set role and client assignments before they first sign in.',
        'Invite на email → accept-invite. Роль и клиенты — до первого входа.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_team'],
    },
  ],
});

registerPageTour({
  id: 'admin-settings',
  match: (p) => p === '/admin/settings',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Organization settings', 'Настройки организации'),
      body: M(
        'Branding and defaults for the whole agency. Client portal (/my) uses logo and colors from here.',
        'Брендинг и defaults агентства. Портал /my использует логотип и цвета отсюда.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_organization'],
    },
    {
      id: 'branding',
      selector: '[data-tour="settings-branding"]',
      title: M('Branding', 'Брендинг'),
      body: M(
        'Upload logo, button presets, portal colors, timezone, and support email shown to clients.',
        'Логотип, наборы кнопок, цвета портала, timezone и support email для клиентов.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_organization'],
    },
  ],
});

registerPageTour({
  id: 'admin-time-tracker',
  match: (p) => p === '/admin/time-tracker',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Time tracker', 'Учёт времени'),
      body: M(
        'Retainer hours per client, billable entries, email warnings, and QuickBooks sync.',
        'Часы ретейнера, записи времени, email и QuickBooks sync.',
      ),
      roles: [ROLES.ADMIN, ROLES.MANAGER],
      capabilities: ['time_tracker'],
    },
    {
      id: 'tabs',
      selector: '[data-tour="time-tabs"]',
      title: M('Tabs', 'Вкладки'),
      body: M(
        'Clients — limits and sync, Notifications — Mailgun templates, History — sent emails log.',
        'Clients — лимиты, Notifications — Mailgun, History — журнал писем.',
      ),
      roles: [ROLES.ADMIN, ROLES.MANAGER],
      capabilities: ['time_tracker'],
    },
  ],
});

registerPageTour({
  id: 'admin-emails',
  match: (p) => p === '/admin/emails',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Emails', 'Почта'),
      body: M(
        'Your own Outlook inbox via Microsoft Graph. Connect Graph in Integrations; each team member’s portal email must be their Microsoft 365 address.',
        'Ваш ящик Outlook через Microsoft Graph. Подключите Graph в Integrations; почта в профиле должна совпадать с адресом Microsoft 365.',
      ),
      roles: STAFF,
      capabilities: ['view_mailbox'],
    },
    {
      id: 'list',
      selector: '[data-tour="mailbox-list"]',
      title: M('Message list', 'Список писем'),
      body: M(
        'Open a message here to read it, reply, reply all, or forward. Nothing opens in Outlook.',
        'Откройте письмо здесь: чтение, ответ, ответ всем и пересылка. Outlook отдельно не нужен.',
      ),
      roles: STAFF,
      capabilities: ['view_mailbox'],
    },
  ],
});

registerPageTour({
  id: 'admin-meetings',
  match: (p) => p === '/admin/meetings',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Meetings', 'Встречи'),
      body: M(
        'Your Outlook calendar, the same mailbox as Emails.',
        'Ваш календарь Outlook — тот же ящик, что и в Emails.',
      ),
      roles: STAFF,
      capabilities: ['view_mailbox'],
    },
    {
      id: 'list',
      selector: '[data-tour="mailbox-list"]',
      title: M('Upcoming meetings', 'Предстоящие встречи'),
      body: M(
        'Helps staff prepare for client calls. Clients with portal access see their subset under /my/meetings.',
        'Подготовка к звонкам. Клиенты видят своё в /my/meetings.',
      ),
      roles: STAFF,
      capabilities: ['view_mailbox'],
    },
  ],
});

registerPageTour({
  id: 'admin-chat',
  match: (p) => p === '/admin/chat',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Live chat inbox', 'Inbox live chat'),
      body: M(
        'Staff-side conversations with portal clients and guests. Requires live_chat plan and chat-service running.',
        'Переписка staff с клиентами и гостями. Нужны live_chat и chat-service.',
      ),
      roles: STAFF,
      capabilities: ['live_chat'],
    },
    {
      id: 'panel',
      selector: '[data-tour="chat-panel"]',
      title: M('Conversations', 'Диалоги'),
      body: M(
        'Pick a thread, reply in real time. OpenAI can assist when configured. Unread counts appear in notifications.',
        'Выберите диалог и отвечайте. OpenAI при настройке. Непрочитанное — в уведомлениях.',
      ),
      roles: STAFF,
      capabilities: ['live_chat'],
    },
  ],
});

registerPageTour({
  id: 'admin-notifications',
  match: (p) => p === '/admin/notifications',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Notifications', 'Уведомления'),
      body: M(
        'Full history of in-app alerts. Bell icon in header shows recent items; configure email/push in Profile.',
        'История in-app алертов. Колокольчик — недавние; email/push — в Profile.',
      ),
      roles: STAFF,
    },
    {
      id: 'list',
      selector: '[data-tour="notifications-list"]',
      title: M('Alert list', 'Список алертов'),
      body: M(
        'Tasks, forms, assets, chat, and time tracker events appear here based on your role.',
        'Задачи, формы, ассеты, чат и time tracker — по вашей роли.',
      ),
      roles: STAFF,
    },
  ],
});

registerPageTour({
  id: 'admin-ghl-locations',
  match: (p) => p === '/admin/ghl-locations',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('GHL Locations', 'Локации GHL'),
      body: M(
        'Link Go High Level sub-accounts to portal clients. Set pipelines and sync per location.',
        'Привязка sub-account GHL к клиентам портала. Pipeline и sync для каждой локации.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_integrations'],
    },
    {
      id: 'import',
      selector: '[data-tour="ghl-import-panel"]',
      title: M('Import panel', 'Панель импорта'),
      body: M(
        'Import contacts and opportunities from GHL into linked portal clients.',
        'Импорт контактов и opportunities из GHL в привязанных клиентов портала.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_integrations'],
    },
    {
      id: 'table',
      selector: '[data-tour="ghl-locations-table"]',
      title: M('Location table', 'Таблица локаций'),
      body: M(
        'Each row links one GHL location to a portal client. Choose pipeline, default stage, and enable sync.',
        'Каждая строка связывает локацию GHL с клиентом портала. Pipeline, стадия по умолчанию и sync.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['manage_integrations'],
    },
  ],
});

registerPageTour({
  id: 'admin-audit',
  match: (p) => p === '/admin/audit-logs',
  steps: [
    {
      id: 'intro',
      selector: '[data-tour="page-title"]',
      title: M('Activity log', 'Журнал действий'),
      body: M(
        'See who did what and when — clients, projects, team, integrations, and more. Visible to company admins.',
        'Кто, когда и что сделал: клиенты, проекты, команда, интеграции и другое. Видят админы агентства.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['company_admin'],
    },
    {
      id: 'list',
      selector: '[data-tour="audit-list"]',
      title: M('Log entries', 'Записи журнала'),
      body: M(
        'Each entry shows the person, time, and action. Filter by user, category, or date.',
        'В каждой записи — кто, когда и что сделал. Фильтры: пользователь, категория, дата.',
      ),
      roles: [ROLES.ADMIN],
      capabilities: ['company_admin'],
    },
  ],
});
